import { useQuery } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import { logException } from 'App/logging';
import { OnboardingSupportOptions } from './graphql/__generated__/OnboardingSupportOptions';
import { WelcomeModalStatus } from './graphql/__generated__/WelcomeModalStatus';
import { ONBOARDING_SUPPORT_OPTIONS_QUERY } from './graphql/onboardingSupportOptions';
import { WELCOME_MODAL_STATUS_QUERY } from './graphql/welcomeModalStatus';
import { isWithinReopenWindow } from './reopenWindow';
import {
  getDescribableOptions,
  MINIMUM_SUPPORT_OPTIONS,
} from './supportOptions';

/**
 * Opens the welcome modal again on request, whatever the member answered the
 * first time.
 *
 * Kept apart from `useWelcomeModal` because a reopen records nothing:
 * `welcomeStatus` describes the automatic open, and a second look must not
 * overwrite it.
 *
 * Offered only in the member's first 14 days. The offerings are fetched up
 * front inside that window, so callers know whether to offer a reopen at all,
 * and not at all outside it.
 */
export const useWelcomeModalReopen = () => {
  const [isOpen, setIsOpen] = useState(false);

  const isActive = Boolean(useFeatureFlag(FEATURE_FLAGS.WELCOME_MODAL_ENABLED));

  const {
    data: statusData,
    loading: isStatusLoading,
    error: statusError,
  } = useQuery<WelcomeModalStatus>(WELCOME_MODAL_STATUS_QUERY, {
    skip: !isActive,
  });

  const isInWindow =
    !statusError &&
    isWithinReopenWindow(statusData?.user?.activationDate, new Date());

  const {
    data: optionsData,
    loading: isOptionsLoading,
    error: optionsError,
  } = useQuery<OnboardingSupportOptions>(ONBOARDING_SUPPORT_OPTIONS_QUERY, {
    skip: !isActive || !isInWindow,
    onError: error =>
      logException(error, {
        extra: { reason: "Could not read the member's support options" },
      }),
  });

  const supportOptions = optionsData?.onboardingSupportOptions;
  const options = useMemo(
    () => getDescribableOptions(supportOptions?.options ?? []),
    [supportOptions],
  );

  const canReopen =
    isActive &&
    isInWindow &&
    !optionsError &&
    options.length >= MINIMUM_SUPPORT_OPTIONS;

  return {
    canReopen,
    isLoading:
      (isStatusLoading && !statusData) ||
      (isInWindow && isOptionsLoading && !optionsData),
    isOpen: canReopen && isOpen,
    firstName: statusData?.user?.firstName,
    options,
    practicalSupport: supportOptions?.practicalSupport ?? null,
    open: useCallback(() => setIsOpen(true), []),
    close: useCallback(() => setIsOpen(false), []),
  };
};
